import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

interface CompetitorVideos {
  total_running: number;
  by_competitor: Record<string, number>;
}

interface CompetitorAnalysisProps {
  competitor_videos: CompetitorVideos;
}

const CompetitorAnalysis: React.FC<CompetitorAnalysisProps> = ({
  competitor_videos,
}) => {
  // Transform data for the pie chart
  const chartData = Object.entries(competitor_videos.by_competitor).map(
    ([competitor, count]) => ({
      name: competitor,
      value: count as number,
      percentage: (
        ((count as number) / competitor_videos.total_running) *
        100
      ).toFixed(1),
    })
  );

  // Generate colors for the pie chart
  const generateColors = (count: number) => {
    const baseColors = [
      "#F97316",
      "#FB923C",
      "#FBBF24",
      "#FCD34D",
      "#FED7AA",
      "#FEEBC3",
      "#FFDCA8",
      "#C2410C",
      "#9A3412",
      "#7C2D12",
      "#D97706",
      "#B45309",
    ];

    const colors = [];
    for (let i = 0; i < count; i++) {
      colors.push(baseColors[i % baseColors.length]);
    }
    return colors;
  };

  const colors = generateColors(chartData.length);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="font-semibold text-gray-900">{data.name}</p>
          <p className="text-sm text-gray-600">
            {data.value} videos ({data.percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Competitor Analysis
          </h3>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-50 text-orange-700 border border-orange-200">
              <div className="w-2 h-2 bg-orange-400 rounded-full mr-2"></div>
              {competitor_videos.total_running} Total Running
            </span>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 min-h-[400px]">
          {/* Pie Chart */}
          <div className="flex flex-col justify-center items-center">
            <div className="w-full max-w-sm mx-auto">
              <div className="relative aspect-square min-h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart
                    margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                  >
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={70}
                      outerRadius={120}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={colors[index]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Custom Legend below chart */}
              <div className="mt-4 px-2">
                <div className="flex flex-wrap justify-center gap-3">
                  {chartData.slice(0, 6).map((item, index) => (
                    <div key={item.name} className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: colors[index] }}
                      />
                      <span className="text-xs text-gray-600 truncate max-w-20">
                        {item.name}
                      </span>
                    </div>
                  ))}
                </div>
                {chartData.length > 6 && (
                  <div className="text-center mt-2">
                    <span className="text-xs text-gray-500">
                      +{chartData.length - 6} more
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Statistics List */}
          <div className="flex flex-col">
            <h4 className="font-semibold text-gray-900 mb-4">
              Competitor Breakdown
            </h4>
            <div className="flex-1 max-h-80 overflow-y-auto space-y-2">
              {chartData
                .sort((a, b) => b.value - a.value)
                .map((item, index) => (
                  <div
                    key={item.name}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div
                        className="w-4 h-4 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor:
                            colors[
                              chartData.findIndex((d) => d.name === item.name)
                            ],
                        }}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 truncate">
                          {item.name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {item.percentage}% of total
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-3">
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium bg-orange-50 text-orange-700 border border-orange-200">
                        {item.value}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompetitorAnalysis;
